import { crosstalkColumns } from '../crosstalk/constants';
import { DatasetFetchRuntimeParams, Query } from '../query';
import { MetadataAndQueryURLsConfig } from './metadata-and-query-urls-config';
import { CrosstalkOptions, NamedQueryInit, PeriodType, RuntimeParameter } from './named-query-init';
import { QueryType } from './query-type';

export class NamedQuery {
    id: string;
    name: string;
    tags: string;
    template: string;
    periodType: PeriodType;
    type: QueryType;
    isOptIn: boolean;
    clientCodes: string[];
    createdDate?: Date;
    createdBy?: string;
    updatedDate?: Date | null;
    updatedBy?: string | null;
    crosstalkOptions?: CrosstalkOptions;
    parameters?: RuntimeParameter[];

    private unionQueries: string[] = [];

    constructor(init: NamedQueryInit, config: MetadataAndQueryURLsConfig) {
        this.id = init.id;
        this.name = init.name ?? '';
        this.tags = init.tags ?? '';
        this.template = init.template ?? '';
        this.periodType = init.periodType;
        this.type = init.type;
        this.isOptIn = init.isOptIn;
        this.clientCodes = init.clientCodes ?? [];
        this.createdDate = init.createdDate;
        this.createdBy = init.createdBy;
        this.updatedDate = init.updatedDate;
        this.updatedBy = init.updatedBy;
        this.crosstalkOptions = init.crosstalkOptions;
        this.parameters = init.parameters;

        this.periodType = init.periodType;
        this.type = this.getQueryType(init.type, config);
    }

    getDummyRuntimeParams(): DatasetFetchRuntimeParams {
        return {
            timeseriesGranularity: '',
            dateRange: { from: 'EOD Date', to: 'EOD Date' },
            fundCodes: [],
            includeComments: true,
            includeLinks: true,
            clientCodeList: [],
            specialCaseParams: {},
            additionalFilters: undefined,
        };
    }

    populateStackedQueryTemplate(params: DatasetFetchRuntimeParams): Query[] {
        this.unionQueries = this.normalizeQueryTemplate().split('@@UNION@@');

        return this.unionQueries.map((queryTemplate) => {
            return convertQueryTemplateToQuery(
                queryTemplate,
                params,
                this.normalizeConversableType());
        });
    }

    normalizeConversableType(): string | undefined {
        return this.crosstalkOptions?.conversableType;
    }

    normalizeQueryTypeName(): string {
        return this.type.name;
    }

    normalizeQueryTemplate(): string {
        return this.template;
    }

    normalizeQueryEndpoint(): string {
        return this.type.dataEndpoint;
    }

    normalizeQueryIsStacked(): boolean {
        return this.template.indexOf('@@UNION@@') !== -1;
    }

    normalizeStatusEndpoint(): string {
        return this.type.statusEndpoint;
    }

    isAdhoc(): boolean {
        return this.type.dataEndpoint.toLowerCase().includes('adhoc');
    }

    private getQueryEndpoint(replaceHostInURLs: boolean, apiLocation: string): string {
        const dataEndpoint = this.type.dataEndpoint;
        if (replaceHostInURLs) { // since we are getting a URL to Trebek RND for localhost, we need to adjust
            const pathname = new URL(dataEndpoint).pathname;
            const dataEndpointPath = pathname.substring(pathname.indexOf('rest'), pathname.length);
            return `${apiLocation}/trebek/api/${dataEndpointPath}`;
        }
        return dataEndpoint;
    }

    private getMetadataEndpoint(replaceHostInURLs: boolean, apiLocation: string): string {
        const metadataEndpoint = this.type.metadataEndpoint;
        if (replaceHostInURLs) { // since we are getting a URL to NQS RND for localhost, we need to adjust
            const metadataEndpointPath = new URL(metadataEndpoint).pathname;
            return `${apiLocation}${metadataEndpointPath}`;
        }
        return metadataEndpoint;
    }

    private getStatusEndpoint(replaceHostInURLs: boolean, apiLocation: string): string {
        const statusEndpoint = this.type.statusEndpoint;
        if (replaceHostInURLs) { // since we are getting a URL to Trebek RND for localhost, we need to adjust
            const pathname = new URL(statusEndpoint).pathname;
            const decodedPathname = decodeURIComponent(pathname);
            const dataEndpointPath = decodedPathname.substring(decodedPathname.indexOf('rest'), decodedPathname.length);
            return `${apiLocation}/trebek/api/${dataEndpointPath}`;
        }
        return statusEndpoint;
    }

    private getQueryType(queryType: QueryType, config: MetadataAndQueryURLsConfig): QueryType {
        return {
            ...queryType,
            dataEndpoint: this.getQueryEndpoint(config.replaceHostInURLs, config.trebekApiLocation),
            metadataEndpoint: this.getMetadataEndpoint(config.replaceHostInURLs, config.cfbApiLocation),
            statusEndpoint: this.getStatusEndpoint(config.replaceHostInURLs, config.trebekApiLocation),
        };
    }
}

function convertQueryTemplateToQuery(
    queryTemplate: string,
    params: DatasetFetchRuntimeParams,
    conversableType: string | undefined,
): Query {
    let escapedTemplate = checkForNullSafeReplace(queryTemplate, 'timeseriesRange.from', params.dateRange.from);
    escapedTemplate = checkForNullSafeReplace(escapedTemplate, 'timeseriesRange.to', params.dateRange.to);
    escapedTemplate = checkForNullSafeReplace(escapedTemplate, 'timeseriesRange.granularity', params.timeseriesGranularity?.toLowerCase());
    escapedTemplate = checkForNullSafeReplace(escapedTemplate, 'includeLinks', params.includeLinks);
    escapedTemplate = checkForNullSafeReplace(escapedTemplate, 'fundCodeList', JSON.stringify(params.fundCodes));

    const query = JSON.parse(escapedTemplate) as Query;

    if (query?.timeseriesRange?.granularity) {
        query.timeseriesRange.granularity = query.timeseriesRange.granularity.toLowerCase();
    }

    if (Object.prototype.hasOwnProperty.call(params.specialCaseParams, 'includeManuallyReleased')) {
        query.includeManuallyReleased = params.specialCaseParams.includeManuallyReleased;
    }

    if (Object.prototype.hasOwnProperty.call(params.specialCaseParams, 'acknowledged')) {
        query.acknowledged = params.specialCaseParams.acknowledged;
    }

    if (conversableType) {
        // Some datasets, like adhoc, don't have the "selectedColumns" property
        // and for those Trebek is automatically adding the crosstalk columns to the response
        // without us requesting them, we just need to add the "conversationType"
        query.selectedColumns?.push(...crosstalkColumns, { columnId: 'conversationId' });
        query.conversationType = conversableType;
    }

    return query;
}

function checkForNullSafeReplace(s: string, key: string, value?: string | null | boolean): string {
    if (value == null || value === 'null') {
        return s;
    }

    let escaped = s;
    while (escaped.indexOf(`@@${key}@@`) !== -1) {
        escaped = nullSafeReplace(escaped, key, value);
    }

    return escaped;
}

function nullSafeReplace(s: string, key: string, value?: string | null | boolean): string {
    if (value == null || value === 'null') {
        return s;
    }

    return s.replace(`@@${key}@@`, `${value}`);
}
